import {useCallback, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
} from 'chart.js';
import KycStatsTab from './tabs/KycStatsTab';
import FinancialMetricsTab from './tabs/FinancialMetricsTab';
import AssetClassAndDabaProTab from './tabs/AssetClassAndDabaProTab';
import UserGrowthTab from './tabs/UserGrowthTab';
import {ReferralChannelStats} from './tabs/ReferralChannelStats';
import InvestorsRegionsTab from './tabs/InvestorsRegionsTab';
import TabRail from '../shared/TabRail';
import styles from '../shared/dashboard.module.scss';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
);

/**
 * The super-admin dashboard shell.
 *
 * The tab labels are shorter than they were: "Users / Pro Charts" and "Users /
 * KYC Stats" both began with the same word, and "Subscription / Asset Class
 * Distribution" was long enough on its own to push the sixth tab off screen.
 * The `value`s are unchanged, so links people have already bookmarked with
 * `?tab=` still land where they did.
 */
const TABS = [
  {label: 'Growth', value: 'USERS_STATS'},
  {label: 'Users & KYC', value: 'KYC_STATS'},
  {label: 'Financials', value: 'FINANCIAL_METRICS'},
  {label: 'Subscriptions & Assets', value: 'DABA_PRO_ASSET_CLASS'},
  {label: 'Referral channels', value: 'REFERRAL_CHANNELS'},
  {label: 'Investor regions', value: 'INVESTORS_REGIONS'},
];

const TAB_SUBTITLES = {
  USERS_STATS: 'Signups and Daba Pro subscriptions, day by day',
  KYC_STATS: 'Who is on the platform and how far through verification',
  FINANCIAL_METRICS: 'Money in, money out, and what the platform earned',
  DABA_PRO_ASSET_CLASS: 'What people subscribe to and what they hold',
  REFERRAL_CHANNELS: 'How people say they found daba',
  INVESTORS_REGIONS: 'Where investors and users are',
};

export const SuperAdminOverview = ({handleSwitch}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(location.search).get('tab') || 'USERS_STATS',
  );

  const changeTab = useCallback(
    value => {
      setActiveTab(value);
      const params = new URLSearchParams(location.search);
      params.set('tab', value);
      navigate(
        {pathname: location.pathname, search: params.toString()},
        {replace: true},
      );
    },
    [location.pathname, location.search, navigate],
  );

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <div>
          <h1>Overview</h1>
          <p>{TAB_SUBTITLES[activeTab]}</p>
        </div>
        <button
          type="button"
          className={styles.switchButton}
          onClick={handleSwitch}>
          Switch to default dashboard
        </button>
      </header>

      <TabRail tabs={TABS} activeTab={activeTab} onChange={changeTab} />

      {activeTab === 'USERS_STATS' && <UserGrowthTab />}
      {activeTab === 'KYC_STATS' && <KycStatsTab />}
      {activeTab === 'FINANCIAL_METRICS' && <FinancialMetricsTab />}
      {activeTab === 'DABA_PRO_ASSET_CLASS' && <AssetClassAndDabaProTab />}
      {activeTab === 'REFERRAL_CHANNELS' && <ReferralChannelStats />}
      {activeTab === 'INVESTORS_REGIONS' && <InvestorsRegionsTab />}
    </div>
  );
};
